// O estado do ajuste mês a mês de um alvo da EAP (design D20): busca a referência no servidor
// ao abrir o modo, guarda a série editada e salva tudo de uma vez (`operation: "replace"`).
// O servidor é chamado só ao abrir e ao salvar, nunca por tecla: a edição é local.
//
// Estados da leitura: `idle`, `loading`, `ready`, `unavailable` (a obra não tem origem
// utilizável, com o motivo) e `error` (rede/servidor; a tela oferece tentar de novo). Uma
// resposta de um pedido já substituído nunca sobrescreve o atual.
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  getMonthEditReference,
  replaceTargetAdjustments,
} from "./disbursementApi";
import {
  balanceOf,
  deltaOf,
  moveBetween,
  nextMonthWithin,
  postponeMonth,
  sameSeries,
  seriesMap,
  seriesPairs,
  tableMonths,
} from "./monthEdit";

const IDLE = Object.freeze({ status: "idle", data: null, reason: null });

const useMonthEdit = ({ workId, wbsCodeId, enabled }) => {
  const [load, setLoad] = useState(IDLE);
  const [tick, setTick] = useState(0);
  const [edited, setEdited] = useState(() => new Map());
  const [extraMonths, setExtraMonths] = useState(0);
  const [saving, setSaving] = useState(false);

  const key =
    enabled && workId && wbsCodeId ? JSON.stringify([workId, wbsCodeId]) : null;

  useEffect(() => {
    if (!key) {
      setLoad(IDLE);
      return undefined;
    }
    let current = true;
    setLoad({ status: "loading", data: null, reason: null });
    (async () => {
      try {
        const result = await getMonthEditReference(workId, wbsCodeId);
        if (!current) return;
        if (result?.code === 200) {
          setLoad({ status: "ready", data: result, reason: null });
          // A tabela abre com o ajustado VIGENTE (referência + os ajustes ativos do alvo).
          setEdited(seriesMap(result.currentSeries));
          setExtraMonths(0);
        } else if (result?.errorCode === "month_edit_unavailable") {
          setLoad({
            status: "unavailable",
            data: null,
            reason: result?.details?.reason ?? null,
          });
        } else {
          setLoad({
            status: "error",
            data: null,
            reason: result?.errorCode ?? null,
          });
        }
      } catch {
        if (current) setLoad({ status: "error", data: null, reason: null });
      }
    })();
    return () => {
      current = false;
    };
    // `tick` recarrega a referência (prévia velha, tentar de novo).
  }, [key, tick, workId, wbsCodeId]);

  const data = load.status === "ready" ? load.data : null;
  const reference = useMemo(() => seriesMap(data?.referenceSeries), [data]);
  const current = useMemo(() => seriesMap(data?.currentSeries), [data]);
  const window = data?.window ?? null;
  const months = useMemo(
    () =>
      data
        ? tableMonths({ reference, current, edited, window, extraMonths })
        : [],
    [data, reference, current, edited, window, extraMonths],
  );
  const balance = data ? balanceOf(reference, edited) : 0;
  const changedMonths = data ? deltaOf(reference, edited).size : 0;
  // "Mudou algo" é contra o VIGENTE, não contra a referência: voltar à referência de um alvo
  // que tinha ajuste é uma mudança (remove o ajuste) e pode ser salva.
  const dirty = data ? !sameSeries(current, edited) : false;
  const lastMonth = months[months.length - 1] ?? null;

  const setMonth = useCallback((month, cents) => {
    if (!Number.isSafeInteger(cents) || cents < 0) return;
    setEdited((map) => new Map(map).set(month, cents));
  }, []);

  return {
    status: load.status,
    reason: load.reason,
    data,
    reference,
    current,
    edited,
    months,
    window,
    balance,
    changedMonths,
    dirty,
    saving,
    canAddMonth:
      lastMonth !== null && nextMonthWithin(lastMonth, window) !== null,
    canSave: load.status === "ready" && dirty && balance === 0 && !saving,
    setMonth,
    postpone: (month) => setEdited((map) => postponeMonth(map, month, window)),
    move: (from, to, cents) =>
      setEdited((map) => moveBetween(map, from, to, cents)),
    addMonth: () => setExtraMonths((count) => count + 1),
    undo: () => {
      setEdited(new Map(current));
      setExtraMonths(0);
    },
    backToReference: () => setEdited(new Map(reference)),
    reload: () => setTick((value) => value + 1),
    /** Grava; devolve o envelope da rota (`{ code, ... }`). Prévia velha recarrega a referência. */
    save: async ({ description } = {}) => {
      if (!data) return null;
      setSaving(true);
      try {
        const result = await replaceTargetAdjustments(workId, {
          wbsCodeId,
          editedSeries: seriesPairs(edited),
          previewStamp: data.previewStamp,
          ...(description ? { description } : {}),
        });
        if (result?.errorCode === "month_edit_stale")
          setTick((value) => value + 1);
        return result;
      } finally {
        setSaving(false);
      }
    },
  };
};

export default useMonthEdit;
